import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { Card, CardType, Changes, Disease, Organ } from '../api/types';
import { useResource } from '../api/useResource';
import {
  Field,
  NumberInput,
  Panel,
  Select,
  SlugPicker,
  Spinner,
  Status,
  TextInput,
  Toggle,
  summariseChanges,
} from '../components/ui';

const CARD_TYPES: { value: CardType; label: string }[] = [
  { value: 'MEDICINE', label: 'Medicine' },
  { value: 'SURGICAL', label: 'Surgical' },
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'PREVENTATIVE', label: 'Preventative' },
];

const EMPTY_CARD: Card = {
  id: '',
  code: '',
  name: '',
  type: 'MEDICINE',
  description: '',
  artKey: null,
  copies: 1,
  active: true,
  isWild: false,
  treatsDiseaseSlugs: [],
  treatsOrganSlugs: [],
};

export function CardsPage() {
  const [typeFilter, setTypeFilter] = useState<CardType | ''>('');
  const [query, setQuery] = useState('');
  const cards = useResource(
    () =>
      api<{ cards: Card[] }>(
        `/v1/admin/cards${typeFilter ? `?type=${typeFilter}` : ''}`,
      ),
    [typeFilter],
  );
  const diseases = useResource(() => api<{ diseases: Disease[] }>('/v1/admin/diseases'));
  const organs = useResource(() => api<{ organs: Organ[] }>('/v1/admin/organs'));

  const [editing, setEditing] = useState<Card | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const diseaseOptions = (diseases.data?.diseases ?? []).map((d) => ({ slug: d.slug, name: d.name }));
  const organOptions = (organs.data?.organs ?? []).map((o) => ({ slug: o.slug, name: o.name }));

  // Filtering by name/code happens locally: the whole deck is ~150 rows, and a
  // round trip per keystroke buys nothing.
  const visible = (cards.data?.cards ?? []).filter((card) => {
    const needle = query.trim().toLowerCase();
    return !needle || card.code.includes(needle) || card.name.toLowerCase().includes(needle);
  });

  async function save() {
    if (!editing) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    const body = {
      name: editing.name,
      type: editing.type,
      description: editing.description,
      artKey: editing.artKey,
      copies: editing.copies,
      isWild: editing.isWild,
      active: editing.active,
      treatsDiseaseSlugs: editing.treatsDiseaseSlugs,
      treatsOrganSlugs: editing.treatsOrganSlugs,
    };

    try {
      if (creating) {
        await api('/v1/admin/cards', { method: 'POST', body: { ...body, code: editing.code } });
        setMessage(`Created ${editing.code}.`);
      } else {
        const result = await api<{ changes: Changes }>(`/v1/admin/cards/${editing.code}`, {
          method: 'PATCH',
          body,
        });
        setMessage(summariseChanges(result.changes));
      }

      setEditing(null);
      setCreating(false);
      await cards.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if (cards.loading && !cards.data) return <Spinner />;
  if (cards.error) return <Status error={cards.error} />;

  return (
    <Panel
      title="Cards"
      actions={
        <button
          className="button"
          onClick={() => {
            setEditing({ ...EMPTY_CARD });
            setCreating(true);
          }}
        >
          New card
        </button>
      }
    >
      <p className="muted">
        Wild is a trait, not a fifth type (spec 2.5): a card can treat two diseases without the
        game calling it wild, so the badge is set independently of the treats list. No organs
        selected means the card is playable on any organ.
      </p>

      <div className="row">
        <Select
          value={typeFilter}
          options={[{ value: '' as CardType | '', label: 'All types' }, ...CARD_TYPES]}
          onChange={(value) => setTypeFilter(value)}
        />
        <TextInput value={query} onChange={setQuery} placeholder="Search code or name" />
        <span className="muted">{visible.length} shown</span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Type</th>
            <th>Copies</th>
            <th>Treats</th>
            <th>Wild</th>
            <th>Active</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visible.map((card) => (
            <tr key={card.code} className={card.active ? undefined : 'row-inactive'}>
              <td><code>{card.code}</code></td>
              <td>{card.name}</td>
              <td>{card.type}</td>
              <td>{card.copies}</td>
              <td>{card.treatsDiseaseSlugs.join(', ') || '—'}</td>
              <td>{card.isWild ? 'yes' : ''}</td>
              <td>{card.active ? 'yes' : 'no'}</td>
              <td>
                <button
                  className="button button-ghost"
                  onClick={() => {
                    setEditing({ ...card });
                    setCreating(false);
                  }}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing ? (
        <div className="editor">
          <h3>{creating ? 'New card' : `Editing ${editing.code}`}</h3>

          <div className="grid">
            {creating ? (
              <Field label="Code" hint="lowercase_with_underscores">
                <TextInput value={editing.code} onChange={(code) => setEditing({ ...editing, code })} />
              </Field>
            ) : null}
            <Field label="Name">
              <TextInput value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
            </Field>
            <Field label="Type">
              <Select
                value={editing.type}
                options={CARD_TYPES}
                onChange={(type) => setEditing({ ...editing, type })}
              />
            </Field>
            <Field label="Copies in a fresh deck">
              <NumberInput
                value={editing.copies}
                min={1}
                onChange={(value) => setEditing({ ...editing, copies: value === '' ? 1 : value })}
              />
            </Field>
            <Field label="Art key">
              <TextInput
                value={editing.artKey ?? ''}
                onChange={(artKey) => setEditing({ ...editing, artKey: artKey || null })}
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              className="input"
              rows={3}
              value={editing.description ?? ''}
              onChange={(event) => setEditing({ ...editing, description: event.target.value })}
            />
          </Field>

          <Field label="Treats diseases">
            <SlugPicker
              options={diseaseOptions}
              selected={editing.treatsDiseaseSlugs}
              onChange={(treatsDiseaseSlugs) => setEditing({ ...editing, treatsDiseaseSlugs })}
            />
          </Field>

          <Field label="Restricted to organs" hint="None selected = playable on any organ">
            <SlugPicker
              options={organOptions}
              selected={editing.treatsOrganSlugs}
              onChange={(treatsOrganSlugs) => setEditing({ ...editing, treatsOrganSlugs })}
            />
          </Field>

          <div className="row">
            <Toggle
              checked={editing.isWild}
              label="Wild badge"
              onChange={(isWild) => setEditing({ ...editing, isWild })}
            />
            <Toggle
              checked={editing.active}
              label="Active (in the deck)"
              onChange={(active) => setEditing({ ...editing, active })}
            />
          </div>

          <div className="row">
            <button className="button" onClick={() => void save()} disabled={saving || !editing.name}>
              {saving ? 'Saving…' : creating ? 'Create card' : 'Save card'}
            </button>
            <button
              className="button button-ghost"
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <Status error={error} message={message} />
    </Panel>
  );
}
